<template>
  <v-card>
    <v-card-title :class="['ml-2', { 'highlight-class': isHighlight }]">
      <span class="table-card-title">Resultados por perfil</span>
    </v-card-title>
    <v-data-table
      :items="items"
      :loading="loading"
      :headers="computedHeaders"
      hide-default-footer
      disable-pagination
      class="mr-6 ml-6"
      :no-data-text="computedNoDataText"
    >
      <template v-slot:header.received="{ header }">
        {{ header.text }}
        <mf-tooltip :disabled="!header.tooltip" bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon small color="grey">info</mf-icon>
            </span>
          </template>
          <span>{{ header.tooltip }}</span>
        </mf-tooltip>
      </template>
      <template v-slot:header.total="{ header }">
        {{ header.text }}
        <mf-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon small color="grey">info</mf-icon>
            </span>
          </template>
          <span>Representatividade da oferta dentro dos consumidores que compraram no período.</span>
        </mf-tooltip>
      </template>
      <template v-slot:item="{ item }">
        <tr>
          <td class="text-left">{{ item['text'] }}</td>
          <td v-if="hideQuickActionFromFranchisee" class="text-right">
            {{ item['count'] | localeString }}
          </td>
          <td v-else class="text-right">
            {{ item['count'] | localeString }}
            <mf-quick-actions v-if="item.quickActionsProps && !isMercapromo && showQuickAction" v-bind="item.quickActionsProps" />
          </td>
          <td class="text-right">
            {{ locale(item['representation'], 2) | localeString(1) | percent }}
          </td>
          <td class="text-right">
            {{ locale(item['revenue'], 2) | localeString(2) | currency }}
          </td>
          <td class="text-right">
            {{ locale(item['averageTicket'], 2) | hifen | localeString(2) | currency }}
          </td>
          <td class="text-right">{{ item['coupons'] | localeString }}</td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { localeString } from '@/config/helpers.js'
import {
  criteriaGroup,
  campaignReceivedAndPurchasedCriteria,
  campaignReceivedAndPurchasedAnyProductCriteria,
  campaignReceivedAndOnlyPurchasedAnyProductCriteria,
  campaignOpenedAndPurchasedCriteria,
  campaignOpenedAndPurchasedAnyProductCriteria,
  campaignOpenedAndOnlyPurchasedAnyProductCriteria,
  boughtAdvertisedProductsOffersScheduleCriteria,
  activatedProductsOffersScheduleCriteria,
  isInUnitsCriteria,
  boughtFixedPeriodCriteria,
  boughtSomeHighlightOffersProductsCriteria,
  boughtOnlyHighlightOffersProductsCriteria,
} from '@/components/quick-actions/segmentationGroups.js'
import { DateTime } from 'luxon'

export default {
  props: {
    loading: Boolean,
    hideQuickActionFromFranchisee: Boolean,
    metrics: Object,
    baseMetric: {
      type: String,
      default: 'received',
    },
    campaign: {
      type: Object,
      required: true,
    },
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
    isMercapromo: {
      type: Boolean,
      default: false,
    },
    offerType: {
      type: String,
      default: '',
    },
    headers: {
      type: Array,
      default: null,
    },
    isHighlight: {
      type: Boolean,
      default: false,
    },
    noDataText: {
      type: String,
      default: '',
    },
    quickActionActive: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    locale: localeString,
  }),
  computed: {
    showQuickAction() {
      return !['cashback_percentage_offer_sales', 'cashback_absolute_offer_sales', 'points_offer_sales'].includes(this.offerType) && this.quickActionActive
    },
    computedNoDataText() {
      return this.noDataText || `Não há resultados ${this.isHighlight ? 'neste destaque' : ''}`
    },
    computedHeaders() {
      return (
        this.headers ?? [
          { text: '', sortable: false },
          { text: 'Clientes', sortable: false, align: 'right' },
          { text: this.baseMetricAttributes.percentageColumnName, value: this.baseMetricAttributes.base, sortable: false, align: 'right' },
          { text: 'Faturamento', sortable: false, align: 'right' },
          { text: 'Ticket médio', sortable: false, align: 'right' },
          { text: 'Cupons', sortable: false, align: 'right' },
        ]
      )
    },
    baseMetricAttributes() {
      // Campaigns use initial+final date and offers_schedule use start+end date
      const startDate = this.isChannelOrOfferScheduleCampaign ? this.campaign.message.send_date : this.campaign.start_date
      const finalDate = this.campaign.final_date || this.campaign.end_date
      const strStartDate = DateTime.fromFormat(startDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
      const strFinalDate = DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')

      const quickInitialDate = this.isChannelOrOfferScheduleCampaign
        ? DateTime.fromFormat(this.campaign.initial_date, 'yyyy-MM-dd').toFormat('yyyyMMdd')
        : DateTime.fromFormat(startDate, 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const quickFinalDate = DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('yyyyMMdd')

      const offerId = this.$route.params.id
      const units = this.campaign.units
        ? this.campaign.units.map((u) => {
            return {
              dataType: u.dataType,
              value: u.id_erp,
            }
          })
        : []

      let products = []

      if (this.offerType) {
        if (this.offerType === 'combo_offer_sales') {
          products = this.campaign.combo_offer_sales
            .map((combo) => {
              return combo.target_products.map((target) => target.sku)
            })
            .flat()
        } else {
          products = this.metrics?.products_metrics?.table.map((metric) => metric.product.sku)
        }
      } else {
        products = this.campaign?.products?.map((product) => product.sku) ?? this.campaign?.offer_sales?.map((product) => product.sku) ?? []
      }
      switch (this.baseMetric) {
        case 'highlight':
          return {
            percentageColumnName: 'Percentual dos que visualizaram',
            base: 'highlight',
            groups: [
              {
                label: 'Visualizaram e compraram',
                key: 'viewed_and_bought',
                quickActionsProps: {
                  quickActionGroupId: 'viewed_and_bought',
                  shortDescription: `Clientes que visualizaram o destaque ${this.campaign.name} e compraram no período de mensuração`,
                  longDescription: `Clientes que visualizaram o destaque ${this.campaign.name} e compraram no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: () =>
                    criteriaGroup(
                      boughtFixedPeriodCriteria(
                        this.campaign._id,
                        quickInitialDate,
                        quickFinalDate,
                        units.map((unit) => unit.value)
                      )
                    ),
                },
              },
              {
                label: 'Visualizaram e compraram produtos divulgados',
                key: 'viewed_with_products',
                quickActionsProps: {
                  quickActionGroupId: 'viewed_with_products',
                  shortDescription: `Clientes que visualizaram o destaque ${this.campaign.name} e compraram produtos divulgados no período de mensuração`,
                  longDescription: `Clientes que visualizaram o destaque ${this.campaign.name} e compraram produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: () =>
                    criteriaGroup(
                      boughtSomeHighlightOffersProductsCriteria(
                        this.campaign._id,
                        products,
                        quickInitialDate,
                        quickFinalDate,
                        units.map((unit) => unit.value)
                      )
                    ),
                },
              },
              {
                label: 'Visualizaram e compraram apenas produtos divulgados',
                key: 'viewed_with_only_products',
                quickActionsProps: {
                  quickActionGroupId: 'viewed_with_only_products',
                  shortDescription: `Clientes que visualizaram o destaque ${this.campaign.name} e compraram apenas produtos divulgados no período de mensuração`,
                  longDescription: `Clientes que visualizaram o destaque ${this.campaign.name} e compraram apenas produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: () =>
                    criteriaGroup(
                      boughtOnlyHighlightOffersProductsCriteria(
                        this.campaign._id,
                        products,
                        quickInitialDate,
                        quickFinalDate,
                        units.map((unit) => unit.value)
                      )
                    ),
                },
              },
            ],
          }
        case 'received':
          return {
            percentageColumnName: 'Percentual dos que receberam',
            base: 'received',
            groups: [
              {
                label: 'Receberam e compraram',
                key: 'received_and_bought',
                quickActionsProps: {
                  quickActionGroupId: 'received_and_bought',
                  shortDescription: `Clientes que haviam recebido a campanha ${this.campaign.title} e compraram no período de mensuração`,
                  longDescription: `Clientes que haviam recebido a campanha ${this.campaign.title} e compraram no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignReceivedAndPurchasedCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
              {
                label: 'Receberam e compraram produtos divulgados',
                key: 'received_with_products',
                quickActionsProps: {
                  quickActionGroupId: 'received_with_products',
                  shortDescription: `Clientes que haviam recebido a campanha ${this.campaign.title} e compraram produtos divulgados no período de mensuração`,
                  longDescription: `Clientes que haviam recebido a campanha ${this.campaign.title} e compraram produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignReceivedAndPurchasedAnyProductCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
              {
                label: 'Receberam e compraram apenas produtos divulgados',
                key: 'received_with_only_products',
                quickActionsProps: {
                  quickActionGroupId: 'received_with_only_products',
                  shortDescription: `Clientes que haviam recebido a campanha ${this.campaign.title} e compraram apenas produtos divulgados no período de mensuração`,
                  longDescription: `Clientes que haviam recebido a campanha ${this.campaign.title} e compraram apenas produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignReceivedAndOnlyPurchasedAnyProductCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
            ],
          }
        case 'sent':
          return {
            percentageColumnName: 'Percentual dos participantes',
            base: 'received',
            groups: [
              {
                label: 'Compraram',
                key: 'received_and_bought',
                quickActionsProps: {
                  quickActionGroupId: 'received_and_bought',
                  shortDescription: `Clientes que participaram da campanha ${this.campaign.title} e compraram no período de mensuração`,
                  longDescription: `Clientes que participaram da campanha ${this.campaign.title} e compraram no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignReceivedAndPurchasedCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
              {
                label: 'Compraram produtos divulgados',
                key: 'received_with_products',
                quickActionsProps: {
                  quickActionGroupId: 'received_with_products',
                  shortDescription: `Clientes que participaram da campanha ${this.campaign.title} e compraram produtos divulgados no período de mensuração`,
                  longDescription: `Clientes que participaram da campanha ${this.campaign.title} e compraram produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignReceivedAndPurchasedAnyProductCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
              {
                label: 'Compraram apenas produtos divulgados',
                key: 'received_with_only_products',
                quickActionsProps: {
                  quickActionGroupId: 'received_with_only_products',
                  shortDescription: `Clientes que participaram da campanha ${this.campaign.title} e compraram apenas produtos divulgados no período de mensuração`,
                  longDescription: `Clientes que participaram da campanha ${this.campaign.title} e compraram apenas produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignReceivedAndOnlyPurchasedAnyProductCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
            ],
          }
        case 'opened':
          return {
            percentageColumnName: 'Percentual dos que abriram',
            base: 'opened',
            groups: [
              {
                label: 'Abriram e compraram',
                key: 'opened_and_bought',
                quickActionsProps: {
                  quickActionGroupId: 'opened_and_bought',
                  shortDescription: `Clientes que receberam e abriram a campanha ${this.campaign.title} e compraram no período de mensuração`,
                  longDescription: `Clientes que receberam e abriram a campanha ${this.campaign.title} e compraram no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignOpenedAndPurchasedCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
              {
                label: 'Abriram e compraram produtos divulgados',
                key: 'opened_with_products',
                quickActionsProps: {
                  quickActionGroupId: 'opened_with_products',
                  shortDescription: `Clientes que receberam e abriram a campanha ${this.campaign.title} e compraram produtos divulgados no período de mensuração`,
                  longDescription: `Clientes que receberam e abriram a campanha ${this.campaign.title} e compraram produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignOpenedAndPurchasedAnyProductCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
              {
                label: 'Abriram e compraram apenas produtos divulgados',
                key: 'opened_with_only_products',
                quickActionsProps: {
                  quickActionGroupId: 'opened_with_only_products',
                  shortDescription: `Clientes que receberam e abriram a campanha ${this.campaign.title} e compraram apenas produtos divulgados no período de mensuração`,
                  longDescription: `Clientes que receberam e abriram a campanha ${this.campaign.title} e compraram apenas produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
                  segmentationGroup: criteriaGroup([
                    campaignOpenedAndOnlyPurchasedAnyProductCriteria([this.campaign._id]),
                    ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
                  ]),
                },
              },
            ],
          }
        case 'sales':
          return {
            percentageColumnName: 'Percentual dos que compraram',
            base: 'total',
            groups: [
              {
                label: 'Compraram produtos divulgados',
                key: 'with_sales',
                quickActionsProps: {
                  quickActionGroupId: 'with_sales',
                  shortDescription: `Clientes que compraram produtos em oferta`,
                  longDescription: `Clientes que compraram produtos em oferta na campanha ${this.campaign.name} nas ${this.campaign.units.length} lojas`,
                  segmentationGroup: () => criteriaGroup(boughtAdvertisedProductsOffersScheduleCriteria([offerId], units, products)),
                },
              },
              {
                label: 'Compraram apenas produtos divulgados',
                key: 'with_only_sales',
              },
            ],
          }
        case 'activatable':
          return {
            percentageColumnName: 'Percentual dos que compraram',
            base: 'total',
            groups: [
              {
                label: 'Ativaram ofertas',
                key: 'with_activated_sales',
                quickActionsProps: {
                  quickActionGroupId: 'with_activated_sales',
                  shortDescription: `Clientes que ativaram produtos da oferta`,
                  longDescription: `Clientes que ativaram produtos da campanha ${this.campaign.name}`,
                  segmentationGroup: () => criteriaGroup(activatedProductsOffersScheduleCriteria([offerId], units)),
                },
              },
              {
                label: 'Compraram produtos divulgados',
                key: 'with_sales',
                quickActionsProps: {
                  quickActionGroupId: 'with_sales',
                  shortDescription: `Clientes que compraram produtos em oferta`,
                  longDescription: `Clientes que compraram produtos em oferta na campanha ${this.campaign.name} nas ${this.campaign.units.length} lojas`,
                  segmentationGroup: () => criteriaGroup(boughtAdvertisedProductsOffersScheduleCriteria([offerId], units, products)),
                },
              },
              {
                label: 'Compraram apenas produtos divulgados',
                key: 'with_only_sales',
              },
            ],
          }
        default:
          return {}
      }
    },
    items() {
      if (!this.metrics?.clients_metrics) return []

      const { clients_metrics } = this.metrics
      const { groups, base } = this.baseMetricAttributes

      const data = groups.map(({ label, key, quickActionsProps }) => {
        const groupMetrics = clients_metrics[key] || {}
        const baseCount = clients_metrics[base]?.count || 1

        const representation = this.baseMetric === 'highlight' ? groupMetrics.representation || 0 : ((groupMetrics.count || 0) / baseCount) * 100

        return {
          text: label,
          count: groupMetrics.count,
          representation,
          revenue: groupMetrics.revenue,
          averageTicket: groupMetrics.average_ticket,
          coupons: groupMetrics.tickets,
          quickActionsProps,
        }
      })

      const emptyArrayCheck = data.every(
        (item) => item.count === 0 && item.representation === 0 && item.revenue === 0 && item.averageTicket === 0 && item.coupons === 0
      )

      return emptyArrayCheck ? [] : data
    },
    // Validates whether the current campaign is a channel or offer schedule campaign
    isChannelOrOfferScheduleCampaign() {
      return !['sales', 'activatable', 'highlight'].includes(this.baseMetric)
    },
  },
}
</script>
